import { type ComponentProps, memo, useCallback, useEffect, useRef, useState } from 'react';

import { useTrackCreditMeterViewed } from '@air/analytics';
import { Popover } from '@air/primitive-popover';
import { cn } from '@air/utils-tailwind';

import { CreditUsageBreakdown } from '~/components/CreditUsageMeter/CreditUsageBreakdown';
import { CreditUsageMeterBar } from '~/components/CreditUsageMeter/CreditUsageMeterBar';
import { CreditUsageMeterCount } from '~/components/CreditUsageMeter/CreditUsageMeterCount';
import { FIVE_MINUTES } from '~/constants/unitsOfTime';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';

export type CompactCreditUsageMeterProps = Pick<ComponentProps<'div'>, 'className'> & {
  includeMeterBar?: boolean;
  disableHoverCard?: boolean;
};

export const CompactCreditUsageMeter = memo(
  ({ includeMeterBar, disableHoverCard, className }: CompactCreditUsageMeterProps) => {
    const { data: creditStats, dataUpdatedAt, refetch } = useCreditStats();
    const { trackCreditMeterViewed } = useTrackCreditMeterViewed();
    const creditAnalyticsParams = useCreditAnalyticsParams();
    // Track only first view per session to avoid noisy analytics from repeated opens
    const hasTrackedView = useRef(false);

    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
      if (disableHoverCard) {
        setIsOpen(false);
      }
    }, [disableHoverCard]);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (disableHoverCard) {
          return;
        }

        setIsOpen(open);

        if (open && creditAnalyticsParams && !hasTrackedView.current) {
          hasTrackedView.current = true;
          trackCreditMeterViewed({
            location: 'side_nav',
            trigger: 'click',
            ...creditAnalyticsParams,
          });
        }

        // Refetch data on user interaction (if older than 5 minutes)
        if (open && Date.now() - dataUpdatedAt > FIVE_MINUTES) {
          refetch();
        }
      },
      [creditAnalyticsParams, dataUpdatedAt, refetch, disableHoverCard, trackCreditMeterViewed],
    );

    if (!creditStats?.allocated) {
      return null;
    }

    return (
      <Popover
        open={isOpen}
        onOpenChange={handleOpenChange}
        className="w-[250px] rounded-8 p-0"
        trigger={
          <div
            className={cn(
              'fade-in group flex w-full animate-in cursor-default flex-col gap-1 rounded-8 px-2 pt-2 pb-2 duration-200',
              !disableHoverCard && 'cursor-pointer hover:bg-grey-6',
              className,
            )}
          >
            {includeMeterBar && (
              <CreditUsageMeterBar
                allocated={creditStats.allocated}
                used={creditStats.used}
                className={cn(
                  'transition-opacity duration-200',
                  isOpen ? 'opacity-100' : 'opacity-0',
                  !disableHoverCard && 'group-hover:opacity-100',
                )}
              />
            )}
            <CreditUsageMeterCount allocated={creditStats.allocated} used={creditStats.used} size="compact" />
          </div>
        }
      >
        <CreditUsageBreakdown
          allocated={creditStats.allocated}
          used={creditStats.used}
          nextCycleStartAt={creditStats.nextCycleStartAt}
          includeMeterBar={!includeMeterBar}
        />
      </Popover>
    );
  },
);

CompactCreditUsageMeter.displayName = 'CompactCreditUsageMeter';
